import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Search, ExternalLink, Gavel, Loader2 } from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

/**
 * Related Case Search — thin wrapper over GET /api/case-search which proxies
 * CourtListener's public (unauthenticated) search endpoint. Rate limits are
 * shared across all logged-in users, so the UI caps to 20 results per query.
 */
export default function RelatedCaseSearch() {
  const [q, setQ] = useState("");
  const [court, setCourt] = useState("");
  const [dateAfter, setDateAfter] = useState("");
  const [dateBefore, setDateBefore] = useState("");
  const [results, setResults] = useState(null); // null = never searched
  const [meta, setMeta] = useState({ count: 0, page: 1 });
  const [loading, setLoading] = useState(false);

  const search = async (page = 1) => {
    if (!q.trim()) return toast.error("Enter a search query first");
    setLoading(true);
    try {
      const r = await api.get("/case-search", {
        params: {
          q: q.trim(),
          court: court.trim() || undefined,
          date_after: dateAfter || undefined,
          date_before: dateBefore || undefined,
          page,
        },
      });
      setResults(r.data.results || []);
      setMeta({ count: r.data.count || 0, page });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border border-slate-200" data-testid="related-case-search">
      <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2 bg-slate-50">
        <Gavel className="w-5 h-5 text-slate-700" />
        <h2 className="font-serif-display text-2xl text-slate-900">Related Case Search</h2>
        <span className="label-tiny ml-auto">Powered by CourtListener · Free Law Project</span>
      </div>

      <div className="p-6 space-y-5">
        <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <span className="font-medium">Research aid only.</span> Results come directly from
          CourtListener&apos;s public search API. ProSe.Claims does not endorse or verify these opinions.
          Confirm citations from the primary source (usually the court&apos;s official website) before relying
          on them.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-3">
            <Label className="label-tiny mb-1.5 block">Query</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search(1)}
                placeholder='e.g. "breach of implied warranty of habitability"'
                className={`${inputCls} pl-9`}
                data-testid="rcs-q"
              />
            </div>
          </div>
          <div>
            <Label className="label-tiny mb-1.5 block">Court (id)</Label>
            <Input
              value={court}
              onChange={(e) => setCourt(e.target.value)}
              placeholder="e.g. scotus, cand"
              className={inputCls}
              data-testid="rcs-court"
            />
          </div>
          <div>
            <Label className="label-tiny mb-1.5 block">Filed after</Label>
            <Input type="date" value={dateAfter} onChange={(e) => setDateAfter(e.target.value)} className={inputCls} data-testid="rcs-after" />
          </div>
          <div>
            <Label className="label-tiny mb-1.5 block">Filed before</Label>
            <Input type="date" value={dateBefore} onChange={(e) => setDateBefore(e.target.value)} className={inputCls} data-testid="rcs-before" />
          </div>
          <div className="md:col-span-6 flex flex-wrap gap-2 pt-1">
            <Button
              onClick={() => search(1)}
              disabled={loading}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="rcs-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {loading ? "Searching…" : "Search cases"}
            </Button>
            {results && (
              <span className="ml-auto self-center text-xs text-slate-500 tabular-nums" data-testid="rcs-count">
                {loading ? "Loading…" : `${results.length} shown of ${meta.count.toLocaleString()} total · page ${meta.page}`}
              </span>
            )}
          </div>
        </div>

        {results === null ? (
          <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="rcs-empty-initial">
            Enter a search query above to find related opinions in the CourtListener database.
          </div>
        ) : results.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="rcs-empty">
            No matching cases. Try different keywords or remove the date filters.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200" data-testid="rcs-results">
            {results.map((r, i) => (
              <li key={i} className="py-4 flex flex-col md:flex-row md:items-start gap-4" data-testid={`rcs-result-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <div className="font-serif-display text-lg text-slate-900 leading-snug">{r.case_name}</div>
                    {r.status && (
                      <Badge variant="outline" className="rounded-none border-slate-300 text-[10px] font-mono-tech">
                        {r.status}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 tabular-nums">
                    {[r.court_name || r.court, r.date_filed, r.judge && `Judge ${r.judge}`, r.citation]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {r.snippet && (
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed line-clamp-3" data-testid={`rcs-snippet-${i}`}>
                      {r.snippet}
                    </p>
                  )}
                </div>
                <div className="md:w-40 md:shrink-0 flex md:flex-col gap-2 md:items-end">
                  {r.absolute_url && (
                    <a
                      href={r.absolute_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5"
                      data-testid={`rcs-open-${i}`}
                    >
                      Open on CourtListener <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <span className="text-[10px] font-mono-tech text-slate-500 uppercase tracking-widest">
                    {r.source}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {results && results.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-200 pt-4">
            <Button
              variant="outline"
              disabled={meta.page <= 1 || loading}
              onClick={() => search(meta.page - 1)}
              className="rounded-none border-slate-300"
              data-testid="rcs-prev"
            >
              ← Previous
            </Button>
            <span className="text-xs text-slate-500 tabular-nums">Page {meta.page}</span>
            <Button
              variant="outline"
              disabled={results.length < 20 || loading}
              onClick={() => search(meta.page + 1)}
              className="rounded-none border-slate-300"
              data-testid="rcs-next"
            >
              Next →
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
